import React, { useState, useRef } from 'react';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import {
  Button,
  Chip,
  Divider,
  TextField,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useTemplates } from '../hooks/useTemplates';
import { observer } from 'mobx-react-lite';
import { EeInput } from 'components/EeInput/EeInput';
import { EeCheckboxGroup } from 'components/EeCheckboxGroup/EeCheckboxGroup';
import { useLibrary } from '../hooks/useLibrary';
// @ts-ignore
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// @ts-ignore
import * as yup from 'yup';
// @ts-ignore
import { Autocomplete } from '@material-ui/lab';
import { KPIEditorProvider } from './KPIEditorProvider';
import { toJS } from 'mobx';
import { KPI } from 'types/kpi';
import { useSnackbar } from 'hooks/useSnackbar';

function isIncludedInSearch(searchText: string, kpi: any) {
  const name = kpi.name.toLowerCase();
  const text = searchText.toLowerCase();
  return name.includes(text);
}
export const DRAWER_MODE_TITLES = {
  new: 'Create New Template',
  edit: 'Edit Template',
  duplicate: 'Duplicate Template',
} as {
  [key: string]: string;
};

export const KPITemplatesDrawer = observer(
  ({ onClose, onSave, mode, groupedKPIs, groupOptions, typeOptions }: any) => {
    const { models: tmplModels, operations: tmplOperations } = useTemplates();
    const usingLibrary = useLibrary();
    const kpiEditorRef = useRef();

    const [isKPIEditor, setIsKPIEditor] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedKPIs, setSelectedKPIs] = useState(
      usingLibrary.models.kpis.filter((kpi) => {
        return tmplModels.editorTemplate.indicators.includes(kpi.id);
      }),
    );
    const { addSnackbar } = useSnackbar();
    const isTemplateExist = (value: any) => {
      if (mode === 'edit') {
        return tmplModels.templates
          .filter((t) => t.name !== tmplModels.editorTemplate.name)
          .some((t) => t.name === value);
      } else {
        return tmplModels.templates.some((t) => t.name === value);
      }
    };

    const formSchema = yup.object().shape({
      name: yup
        .string()
        .required('Please give the Template a name')
        .test(
          'unique',
          'Template already exists with this name, please choose another',
          (value: any) => !isTemplateExist(value),
        ),
      indicators: yup
        .array()
        .min(1, 'Please assign at least one KPI to the template')
        .required('Please assign at least one KPI to the template'),
    });

    const methods = useForm({
      //@ts-ignore//@ts-ignore
      resolver: yupResolver(formSchema),
      mode: 'all',
      defaultValues: { kpiEditor: [{ type: '%' }] },
    });

    const { control, errors, handleSubmit, setValue, trigger } = methods;

    const saveTemplate = () =>
      handleSubmit((data: any) => onSave({ mode, data }))();

    const saveNewKPI = async (kpis: KPI[]) => {
      setIsSaving(true);
      const result = await usingLibrary.operations.kpi.saveEditorKPIs(
        false,
        kpis,
      );
      if (result.errorCode === 0) {
        const newSelectedKPIs = [...selectedKPIs, ...(result.payload || [])];
        setSelectedKPIs(newSelectedKPIs);
        setValue('indicators', newSelectedKPIs);
        trigger('indicators');
        usingLibrary.operations.loadKPIs().then();

        const kpiNames = kpis.map((kpi) => kpi.name).join(', ');
        const content = `New KPI: ${kpiNames} ${
          kpis.length === 1 ? 'was' : 'were'
        } successfully created.`;
        addSnackbar(content);
      } else {
        addSnackbar('Failed to create KPI');
      }
      setIsKPIEditor(false);
      setIsSaving(false);
    };

    return (
      <div className="nachinka">
        <div className="d-flex align-items-center mt-3">
          <Typography variant="h2" className="flex-grow-1 mr-3">
            {DRAWER_MODE_TITLES[mode]}
          </Typography>
          <Button color="primary" onClick={onClose} className="mr-2">
            Cancel
          </Button>
          <Button
            disabled={isKPIEditor}
            color="primary"
            variant="contained"
            onClick={saveTemplate}
          >
            Save
          </Button>
        </div>
        <Divider style={{ backgroundColor: '#2f3b4c' }} />
        <form onSubmit={handleSubmit(() => {})}>
          <div className="mb-3">
            <label className="text-uppercase fs-12 mb-1">
              Template Name<sup>*</sup>
            </label>
            <Controller
              name="name"
              control={control}
              defaultValue={tmplModels.editorTemplate.name}
              render={(props) => {
                return (
                  <EeInput
                    {...props}
                    // @ts-ignore
                    error={!!errors.name}
                    // @ts-ignore
                    errorText={errors.name?.message}
                    inputProps={{ maxLength: 50 }}
                  />
                );
              }}
            />
          </div>
          <div className="mb-1">
            <label className="text-uppercase fs-12 mb-1">Description</label>
            <Controller
              name="description"
              control={control}
              defaultValue={tmplModels.editorTemplate.description}
              render={(props) => {
                return <EeInput {...props} inputProps={{ maxLength: 50 }} />;
              }}
            />
          </div>

          <div className="mb-4">
            <div className="d-flex align-items-end">
              <label className="text-uppercase fs-12 mb-1 mr-auto">KPI</label>
              <Button
                disabled={isKPIEditor}
                color="primary"
                size="small"
                onClick={() => {
                  usingLibrary.operations.kpi.addNewKPI().then();
                  setIsKPIEditor(true);
                }}
              >
                + CREATE NEW KPI
              </Button>
            </div>
            <Controller
              name="indicators"
              control={control}
              defaultValue={selectedKPIs}
              render={({ onChange, ...props }) => {
                const selectedKPINames = selectedKPIs.map(({ name }) => name);
                return (
                  <Autocomplete
                    noOptionsText="No KPIs"
                    options={usingLibrary.models.kpis.filter(
                      (kpi) => !selectedKPINames.includes(kpi.name),
                    )}
                    popupIcon={<ExpandMoreIcon />}
                    multiple
                    getOptionLabel={(kpi: any) => {
                      return kpi.name;
                    }}
                    renderInput={(params: any) => (
                      <TextField
                        {...params}
                        // @ts-ignore
                        error={!!errors.indicators}
                        // @ts-ignore
                        helperText={errors?.indicators?.message}
                      />
                    )}
                    onChange={(_: any, selectedKPIs: any) => {
                      setSelectedKPIs(selectedKPIs);
                      setValue('indicators', selectedKPIs);
                      trigger('indicators');
                    }}
                    renderTags={(
                      kpis: any,
                      getTagProps: ({ index }: any) => {},
                    ) => {
                      return selectedKPIs.map(({ name }, index: number) => (
                        <Chip label={name} {...getTagProps({ index })} />
                      ));
                    }}
                    {...props}
                  />
                );
              }}
            />
          </div>
          {isKPIEditor ? (
            <>
              <Divider style={{ backgroundColor: '#2f3b4c' }} />
              <div className="fs-14 text-uppercase">Create New KPI</div>
              <KPIEditorProvider
                ref={kpiEditorRef}
                kpis={toJS(usingLibrary.models.editorKPIs)[0]}
                index={0}
                groupOptions={groupOptions}
                typeOptions={typeOptions}
                key={usingLibrary.models.editorKPIs[0].id}
                onChange={() => {}}
                models={usingLibrary.models}
                onSubmit={(data: any) => saveNewKPI(data.kpiEditor)}
              />
              <div className="d-flex justify-content-end mt-2">
                <Button
                  className="mr-3"
                  color="primary"
                  onClick={() => setIsKPIEditor(false)}
                >
                  Cancel
                </Button>
                <Button
                  disabled={isSaving}
                  variant="outlined"
                  onClick={() => {
                    // @ts-ignore
                    kpiEditorRef.current.submit();
                  }}
                >
                  Add to current template
                </Button>
              </div>
              <Divider style={{ backgroundColor: '#2f3b4c' }} />
            </>
          ) : (
            <>
              <Divider style={{ backgroundColor: '#2f3b4c' }} />
              <div className="d-flex align-items-center">
                <div className="d-flex align-items-center flex-grow-1 w-50">
                  <DescriptionOutlinedIcon />
                  <span className="ml-1 fs-14">KPI Library</span>
                </div>
                <EeInput
                  value={tmplModels.editorSearch}
                  onChange={tmplOperations.editor.handleEditorSearch}
                  search
                  type="search"
                  placeholder="Search"
                />
              </div>
              <Divider style={{ backgroundColor: '#2f3b4c' }} />
              {groupedKPIs
                .map((group: any) => ({
                  ...group,
                  kpis: group.kpis.filter((kpi: any) =>
                    isIncludedInSearch(tmplModels.editorSearch, kpi),
                  ),
                }))
                .filter((group: any) => group.kpis.length)
                .map((group: any) => {
                  const selectedKPIIds = selectedKPIs.map(({ id }) => id);
                  const allChecked = group.kpis.every((kpi: any) => {
                    return selectedKPIIds.includes(kpi.id);
                  });
                  return (
                    <EeCheckboxGroup
                      group={{ ...group, checked: allChecked }}
                      items={group.kpis.map((kpi: any) => {
                        const isChecked = selectedKPIs?.some(
                          (sk) => sk.id === kpi.id,
                        );

                        return {
                          ...kpi,
                          checked: isChecked,
                        };
                      })}
                      key={group.name}
                      labelField="name"
                      valueField="id"
                      legend=""
                      onChange={({ items, checked }) => {
                        let existingKPIs = selectedKPIs.map((kpi) => kpi.id);
                        if (checked) {
                          existingKPIs = existingKPIs
                            .filter((eKPI) => !items.includes(eKPI))
                            .concat(items);
                        } else {
                          existingKPIs = existingKPIs.filter(
                            (eKPI) => !items.includes(eKPI),
                          );
                        }
                        const resultKPIs = existingKPIs.map(
                          (eKPI) => usingLibrary.models.kpisByIds[eKPI],
                        );
                        setSelectedKPIs(resultKPIs);
                        setValue('indicators', resultKPIs);
                        trigger('indicators');
                      }}
                    />
                  );
                })}
            </>
          )}
        </form>
      </div>
    );
  },
);
